import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCol,
  IonContent,
  IonGrid,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonLoading,
  IonPage,
  IonRow,
  IonText,
  useIonToast
} from '@ionic/react';
import { alertCircleOutline, logoGoogle, refreshOutline } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.tsx';
import { supabase } from '../lib/supabaseClient.ts';
import { auth } from '../services/api.ts';

const MAX_RETRIES = 5;  // Increased retries
const RETRY_DELAY = 10000;  // 10 seconds between retries
const TIMEOUT = 50000;  // 50 second timeout

const Login = () => {
  const history = useHistory();
  const [present] = useIonToast();
  const { signInWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [error, setError] = useState('');
  const [serverError, setServerError] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    // Check if user is already logged in
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        console.log('🔐 User already logged in on Login page, redirecting to profile');
        history.replace('/app/profile');
      }
    };
    checkSession();
  }, [history]);

  const handleGmailLogin = async () => {
    try {
      setLoading(true);
      setError('');
      await signInWithGoogle();
    } catch (error) {
      console.error('Google login error:', error);
      present({ message: 'Failed to initiate Google login', duration: 3000, color: 'danger' });
      setError('Failed to initiate Google login');
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e?: any) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    setServerError(false);
    setLoading(true);
    try {
      await auth.login(email.trim(), password, TIMEOUT);
      history.replace('/app/profile');
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.includes('Server is still starting up')) {
        setServerError(true);
        if (retryCount < MAX_RETRIES) {
          setTimeout(() => {
            setRetryCount((c) => c + 1);
            handleEmailLogin(undefined);
          }, RETRY_DELAY);
        } else {
          setLoading(false);
        }
        return;
      }
      const status = err?.response?.status;
      if (status === 401) {
        setError('Invalid email or password');
      } else if (!err?.response) {
        setError(msg || 'Unable to reach server. Please check your connection.');
      } else {
        setError(err?.response?.data?.message || 'Failed to sign in');
      }
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setServerError(false);
    handleEmailLogin(undefined);
  };

  return (
    <IonPage>
      <IonContent
        className="ion-padding"
        scrollY={false}
        style={{
          '--background': 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          '--overflow': 'hidden'
        }}
      >
        {/* Background decorative elements */}
        <div style={{
          position: 'absolute',
          top: '-10%',
          right: '-5%',
          width: '50vw',
          height: '50vw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, rgba(99, 102, 241, 0) 70%)',
          zIndex: 0,
          filter: 'blur(40px)'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-10%',
          left: '-5%',
          width: '40vw',
          height: '40vw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(14, 165, 233, 0.15) 0%, rgba(14, 165, 233, 0) 70%)',
          zIndex: 0,
          filter: 'blur(40px)'
        }} />

        <IonGrid style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          <IonRow className="ion-justify-content-center ion-align-items-center w-full">
            <IonCol size="12" sizeMd="8" sizeLg="5" sizeXl="4">
              <div className="glass-card animate-fade-in" style={{ padding: '2rem' }}>
                {/* Header */}
                <div className="ion-text-center mb-4">
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, var(--ion-color-primary) 0%, var(--ion-color-tertiary) 100%)',
                    margin: '0 auto 1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 16px rgba(79, 70, 229, 0.2)'
                  }}>
                    <span style={{
                      fontSize: '24px',
                      fontWeight: '800',
                      color: '#ffffff',
                      letterSpacing: '-0.02em'
                    }}>
                      CT
                    </span>
                  </div>

                  <h1 className="heading-xl mb-4">
                    Welcome Back
                  </h1>

                  <p className="text-body" style={{ maxWidth: '320px', margin: '0 auto' }}>
                    Sign in to access your AI-powered career network
                  </p>
                </div>

                {/* Google Sign-in Button */}
                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={handleGmailLogin}
                  className="ion-margin-bottom"
                  style={{
                    '--border-radius': '12px',
                    '--border-color': 'var(--ion-color-light-shade)',
                    '--border-width': '1px',
                    '--color': 'var(--ion-text-color)',
                    '--background-hover': 'var(--ion-color-light)',
                    height: '50px',
                    fontWeight: '600',
                    fontSize: '15px',
                    textTransform: 'none'
                  }}
                >
                  <IonIcon icon={logoGoogle} slot="start" style={{ fontSize: '20px' }} />
                  Continue with Google
                </IonButton>

                {/* Divider */}
                <div className="flex-center" style={{ margin: '1.5rem 0' }}>
                  <div style={{ height: '1px', background: 'var(--ion-color-light-shade)', flex: 1 }}></div>
                  <span style={{
                    padding: '0 1rem',
                    fontSize: '13px',
                    color: 'var(--ion-color-medium)',
                    fontWeight: '500'
                  }}>
                    or sign in with email
                  </span>
                  <div style={{ height: '1px', background: 'var(--ion-color-light-shade)', flex: 1 }}></div>
                </div>

                {/* Error Messages */}
                {(serverError || error) && (
                  <div style={{
                    background: serverError ? 'var(--ion-color-warning-tint)' : 'var(--ion-color-danger-tint)',
                    color: serverError ? 'var(--ion-color-warning-shade)' : 'var(--ion-color-danger-shade)',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    marginBottom: '1.5rem',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <IonIcon icon={alertCircleOutline} />
                    <div>
                      {serverError ? (
                        <>
                          Server starting up... {retryCount > 0 && `(Attempt ${retryCount}/${MAX_RETRIES})`}
                        </>
                      ) : (
                        error
                      )}
                    </div>
                  </div>
                )}

                {/* Email Form */}
                <form onSubmit={handleEmailLogin}>
                  <div className="mb-4">
                    <IonLabel style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--ion-text-color)',
                      marginBottom: '8px',
                      marginLeft: '4px'
                    }}>
                      Email Address
                    </IonLabel>
                    <div style={{
                      background: 'var(--ion-color-light)',
                      borderRadius: '12px',
                      border: '1px solid transparent',
                      transition: 'all 0.2s ease'
                    }}>
                      <IonInput
                        type="email"
                        value={email}
                        onIonInput={(e) => setEmail(e.detail.value!)}
                        placeholder="you@example.com"
                        required
                        style={{
                          '--padding-start': '16px',
                          '--padding-end': '16px',
                          '--padding-top': '12px',
                          '--padding-bottom': '12px'
                        }}
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <IonLabel style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--ion-text-color)',
                      marginBottom: '8px',
                      marginLeft: '4px'
                    }}>
                      Password
                    </IonLabel>
                    <div style={{
                      background: 'var(--ion-color-light)',
                      borderRadius: '12px',
                      border: '1px solid transparent',
                      transition: 'all 0.2s ease'
                    }}>
                      <IonInput
                        type="password"
                        value={password}
                        onIonInput={(e) => setPassword(e.detail.value!)}
                        placeholder="••••••••"
                        required
                        style={{
                          '--padding-start': '16px',
                          '--padding-end': '16px',
                          '--padding-top': '12px',
                          '--padding-bottom': '12px'
                        }}
                      />
                    </div>
                  </div>

                  <IonButton
                    expand="block"
                    type="submit"
                    fill="solid"
                    color="primary"
                    disabled={loading || (serverError && retryCount >= MAX_RETRIES) || !email || !password}
                    style={{
                      '--border-radius': '12px',
                      '--background': '#4f46e5',
                      '--background-hover': '#4338ca',
                      '--background-activated': '#3730a3',
                      '--color': '#ffffff',
                      height: '50px',
                      fontWeight: '600',
                      fontSize: '15px',
                      textTransform: 'none',
                      marginTop: '1.5rem',
                      '--box-shadow': '0 4px 12px rgba(99, 102, 241, 0.3)'
                    }}
                  >
                    {serverError ? (
                      <>
                        <IonIcon icon={refreshOutline} slot="start" className="spin" />
                        Retrying...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </IonButton>
                </form>
              </div>
            </IonCol>
          </IonRow>
        </IonGrid>

        <IonLoading
          isOpen={loading}
          message={serverError ? 'Connecting to server...' : 'Signing in...'}
          spinner="crescent"
          cssClass="custom-loading"
        />
      </IonContent>
    </IonPage>
  );
};

export default Login;